import flatten from 'lodash/flatten';
import flow from 'lodash/flow';
import invertBy from 'lodash/invertBy';
import pick from 'lodash/pick';
import uniq from 'lodash/uniq';

import {
    Country as CentralCountry,
    Geolocation as CentralGeolocation,
    JobLocation as CentralJobLocation,
    Node as CentralNode,
    NodeCategory as CentralNodeCategory,
    JobPositionsAvailable as CentralJobPositionsAvailable,
} from 'src/typings/central.generated';
import { LocalCategoryMapV2 } from 'src/models/central/central-mapping.model';
import { GeolocationModel } from 'src/models/locations/geolocation.model';
import { LocationModel } from 'src/models/locations/location.model';
import { Job as LocalJob } from 'src/models/jobs/job.model';

interface CentralCategoryMapping {
    [key: string]: CentralNodeCategory[];
}

const invertMappings = (centralCategories: CentralCategoryMapping | undefined) => {
    return invertBy(centralCategories, value => value.map(i => i.id));
};

export const mapCentralCategoriesToLocalIds = (
    node: CentralNode | undefined
): LocalCategoryMapV2 => {
    if (!node) {
        // should never be hit, but I should still handle this better
        return { industries: {}, occupations: {}, typesOfWork: {} };
    }

    const { employmentTypes, industries, occupations } = node.categories;

    const localCategoryMap: LocalCategoryMapV2 = {
        typesOfWork: invertMappings(employmentTypes),
        industries: invertMappings(industries),
        occupations: invertMappings(occupations),
    };

    return localCategoryMap;
};

export const getCentralMappingsV2 = (
    localMap: LocalCategoryMapV2,
    key: keyof LocalCategoryMapV2,
    ids: number[]
) => {
    return flow(flatten, uniq)(ids.map(id => localMap[key][id]?.map(i => Number(i)) || []));
};

export const mapCentralGeolocation = (
    geolocation: GeolocationModel | null | undefined
): CentralGeolocation | undefined =>
    geolocation
        ? {
              east: geolocation.right,
              north: geolocation.top,
              south: geolocation.bottom,
              west: geolocation.left,
          }
        : undefined;

export const mapCentralLocation = (location: LocationModel): CentralJobLocation => ({
    ...pick(location, 'street', 'city', 'region', 'postcode'),
    country: location.countryCode! as CentralCountry,
    geolocation: mapCentralGeolocation(location.geolocation) || {},
});

export const mapPositionsAvailable = (job: LocalJob): CentralJobPositionsAvailable => {
    if (job.positionsAvailableFrom || job.positionsAvailableTo) {
        return {
            from: job.positionsAvailableFrom,
            to: job.positionsAvailableTo,
        };
    }

    switch (job.positionsAvailable) {
        case '1':
        case '10':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9': {
            const value = Number(job.positionsAvailable);
            return {
                from: value,
                to: value,
            };
        }
        case '10-20':
        case '20-50': {
            const fromTo = job.positionsAvailable.split('-').map(x => Number(x));
            return {
                from: fromTo[0],
                to: fromTo[1],
            };
        }
        case 'over 50':
            return {
                from: 50,
            };
        default:
            return {};
    }
};
